import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { AppDispatch, RootState } from 'store'
import { ColorVariant, Flex } from 'styling/GlobalStyles'
import { MediumText, SmallText } from 'styling/TypographyV2'
import {
  getOperationalSiteOrganogram,
  getOperationalTeams,
} from 'features/operationalManagement/operationalManagementSlice'
import {
  IOperationalOrganogramTeam,
  IOperationalOrgUser,
  IOperationalSiteOrganogram,
  TEAM_TYPE_LABEL,
  TeamType,
  getTeamTypeAccent,
} from 'features/operationalManagement/operationalManagement.types'
import { OptionsProps, Table } from 'components/Table/Table'
import { TeamsIcon, UserSilhouetteIcon } from '../icons'
import {
  EmployeeAvatar,
  LegendItem,
  LegendRow,
  OrgStructureHeader,
  SelectedPersonCard,
  TreeChildrenRow,
  TreeNodeBox,
  TreeNodeCount,
  TreeNodeSubtitle,
  TreeNodeTitle,
  TreeNodeWrapper,
  TreeRoot,
  TreeScroll,
} from '../Organogram.styled'

const ORG_STRUCTURE_TEAM_LIMIT = 500

const countAllMembers = (node: IOperationalOrganogramTeam): number => {
  let total = node.members.length
  for (const c of node.children) total += countAllMembers(c)
  return total
}

interface TreeNodeProps {
  team: IOperationalOrganogramTeam
  selectedId: number | null
  onSelect: (team: IOperationalOrganogramTeam) => void
  isRoot?: boolean
}

const TreeNode = ({ team, selectedId, onSelect, isRoot }: TreeNodeProps) => {
  const theme = useTheme() as any
  const accent = getTeamTypeAccent(theme, team.type)
  const typeLabel = TEAM_TYPE_LABEL[team.type]
  const total = countAllMembers(team)

  return (
    <TreeNodeWrapper className={isRoot ? 'root' : ''}>
      <TreeNodeBox
        accentColor={accent}
        selected={selectedId === team.id}
        onClick={() => onSelect(team)}
      >
        <TreeNodeTitle>{team.name}</TreeNodeTitle>
        <TreeNodeSubtitle>{typeLabel}</TreeNodeSubtitle>
        <TreeNodeCount>
          <TeamsIcon size={14} />
          {total} {total === 1 ? 'person' : 'people'}
        </TreeNodeCount>
      </TreeNodeBox>
      {team.children.length > 0 && (
        <TreeChildrenRow>
          {team.children.map((child) => (
            <TreeNode
              key={child.id}
              team={child}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </TreeChildrenRow>
      )}
    </TreeNodeWrapper>
  )
}

const findNode = (
  nodes: IOperationalOrganogramTeam[],
  id: number,
): IOperationalOrganogramTeam | null => {
  for (const n of nodes) {
    if (n.id === id) return n
    const found = findNode(n.children, id)
    if (found) return found
  }
  return null
}

export const OrgStructureTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const theme = useTheme() as any
  const { teams, isLoadingTeams, organogramBySiteId, loadingOrganogramSiteIds } = useSelector(
    (state: RootState) => state.operationalManagement,
  )

  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)
  const [highlightedMemberId, setHighlightedMemberId] = useState<number | null>(null)

  useEffect(() => {
    dispatch(
      getOperationalTeams({
        page: 1,
        limit: ORG_STRUCTURE_TEAM_LIMIT,
      }),
    )
  }, [])

  const siteIds = useMemo(() => {
    const ids = new Set<number>()
    for (const team of teams.data) {
      if (team.operationalSite?.id != null) ids.add(team.operationalSite.id)
    }
    return Array.from(ids)
  }, [teams.data])

  useEffect(() => {
    for (const siteId of siteIds) {
      if (organogramBySiteId[siteId] || loadingOrganogramSiteIds.includes(siteId)) continue
      dispatch(getOperationalSiteOrganogram(siteId))
    }
  }, [dispatch, siteIds, organogramBySiteId, loadingOrganogramSiteIds])

  const organogram = useMemo<IOperationalSiteOrganogram | undefined>(() => {
    if (siteIds.length === 0) return { teams: [] }
    const roots: IOperationalOrganogramTeam[] = []
    for (const siteId of siteIds) {
      const data = organogramBySiteId[siteId]
      if (data?.teams) roots.push(...data.teams)
    }
    if (roots.length === 0 && siteIds.some((id) => organogramBySiteId[id] == null)) return undefined
    return { teams: roots }
  }, [siteIds, organogramBySiteId])

  const loading =
    isLoadingTeams || siteIds.some((id) => loadingOrganogramSiteIds.includes(id))

  const selectedTeam = useMemo<IOperationalOrganogramTeam | null>(() => {
    if (!organogram || selectedTeamId == null) return null
    return findNode(organogram.teams, selectedTeamId)
  }, [organogram, selectedTeamId])

  const memberTableOptions = useMemo<OptionsProps<IOperationalOrgUser>[]>(
    () => [
      { header: 'Name', cell: (m) => `${m.firstName} ${m.lastName}` },
      { header: 'Department', cell: (m) => m.department?.name ?? '—' },
      { header: 'Role', cell: (m) => m.currentRole?.name ?? '—' },
    ],
    [],
  )

  const legendTypes: TeamType[] = useMemo(() => {
    const present = new Set<TeamType>()
    const walk = (n: IOperationalOrganogramTeam) => {
      present.add(n.type)
      n.children.forEach(walk)
    }
    organogram?.teams.forEach(walk)
    return Array.from(present).sort((a, b) => a - b)
  }, [organogram])

  const lead: IOperationalOrgUser | null = useMemo(() => {
    if (!selectedTeam) return null
    if (selectedTeam.supervisors.length > 0) {
      const u = selectedTeam.supervisors[0].user
      return {
        id: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        department: null,
        currentRole: null,
      }
    }
    return selectedTeam.members[0] ?? null
  }, [selectedTeam])

  return (
    <>
      {legendTypes.length > 0 && (
        <OrgStructureHeader>
          <LegendRow>
            {legendTypes.map((t) => (
              <LegendItem key={t} accentColor={getTeamTypeAccent(theme, t)}>
                {TEAM_TYPE_LABEL[t]}
              </LegendItem>
            ))}
          </LegendRow>
        </OrgStructureHeader>
      )}

      <TreeScroll>
        {loading && !organogram ? (
          <SmallText colorVariant={ColorVariant.SECONDARY}>Loading…</SmallText>
        ) : organogram && organogram.teams.length === 0 ? (
          <SmallText colorVariant={ColorVariant.SECONDARY}>No teams yet.</SmallText>
        ) : organogram ? (
          <TreeRoot>
            <Flex gap='2rem' alignStart>
              {organogram.teams.map((root) => (
                <TreeNode
                  key={root.id}
                  team={root}
                  selectedId={selectedTeamId}
                  onSelect={(t) => {
                    setSelectedTeamId(t.id)
                    setHighlightedMemberId(null)
                  }}
                  isRoot
                />
              ))}
            </Flex>
          </TreeRoot>
        ) : null}
      </TreeScroll>

      {selectedTeam && (
        <>
          <SelectedPersonCard>
            <EmployeeAvatar>
              <UserSilhouetteIcon />
            </EmployeeAvatar>
            <Flex column>
              <Flex center gap='0.5rem'>
                <MediumText bold>
                  {lead
                    ? `${lead.firstName} ${lead.lastName}`
                    : 'No supervisor assigned'}
                </MediumText>
                <SmallText hexColor={getTeamTypeAccent(theme, selectedTeam.type)}>
                  • {selectedTeam.name}
                </SmallText>
              </Flex>
              <SmallText colorVariant={ColorVariant.SECONDARY}>
                {TEAM_TYPE_LABEL[selectedTeam.type]} ·{' '}
                {countAllMembers(selectedTeam)} people
              </SmallText>
            </Flex>
          </SelectedPersonCard>

          <Table<IOperationalOrgUser>
            cellKey='id'
            items={selectedTeam.members}
            emptyListOptions={{ title: 'No members in this team.' }}
            onClick={(m) => setHighlightedMemberId(m.id)}
            isRowHighlighted={(m) => highlightedMemberId === m.id}
            options={memberTableOptions}
          />
        </>
      )}
    </>
  )
}
